import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { MapPin, Save, ToggleLeft, ToggleRight } from "lucide-react";

/**
 * RN-I · Admin control for the coarse-location boost radius on the RN
 * shopper app. The radius powers the "nearby offers first" ranker on
 * `/api/v1/mobile/home`. Never filters — only re-ranks.
 *
 * RN-I.2 — also hosts the platform-funded location-only cashback bonus
 * config (kill-switch, amount, min purchase, message).
 */
export default function DiscoveryLocationSettings() {
  const [config, setConfig] = useState(null);
  const [radiusKm, setRadiusKm] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [saving, setSaving] = useState(false);
  // Location bonus config
  const [bonus, setBonus] = useState(null);
  const [bonusForm, setBonusForm] = useState({
    enabled: false, amount: "", currency: "CAD",
    min_purchase: "", message: "",
  });
  const [bonusSaving, setBonusSaving] = useState(false);
  const [loadErr, setLoadErr] = useState({ geo: false, bonus: false });

  const loadAll = () => {
    setLoadErr({ geo: false, bonus: false });
    api.get("/admin/settings/discovery-geo").then((r) => {
      setConfig(r.data);
      setRadiusKm(String(r.data.radius_km));
      setEnabled(!!r.data.enabled);
    }).catch(() => setLoadErr((x) => ({ ...x, geo: true })));
    api.get("/admin/settings/location-bonus").then((r) => {
      setBonus(r.data);
      setBonusForm({
        enabled: !!r.data.enabled,
        amount: String(r.data.amount ?? ""),
        currency: r.data.currency || "CAD",
        min_purchase: String(r.data.min_purchase ?? ""),
        message: r.data.message || "",
      });
    }).catch(() => setLoadErr((x) => ({ ...x, bonus: true })));
  };
  useEffect(() => { loadAll(); }, []);

  const save = async () => {
    const rk = Number(radiusKm);
    if (!Number.isFinite(rk) || rk < 1 || rk > 500) {
      toast.error("Radius must be between 1 and 500 km");
      return;
    }
    setSaving(true);
    try {
      const r = await api.patch("/admin/settings/discovery-geo", {
        radius_km: rk, enabled,
      });
      setConfig(r.data);
      toast.success("Geo settings saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 max-w-3xl" data-testid="admin-discovery-geo">
      <div className="flex items-center gap-2 mb-1">
        <MapPin className="w-5 h-5 text-cyan-500" />
        <h1 className="font-display text-2xl tracking-tight text-slate-900">Discovery · Coarse location</h1>
      </div>
      <p className="text-sm text-slate-500 mb-6">
        Controls the "nearby offers first" boost in the shopper app. Location
        never filters — this only re-ranks offers within the given radius.
      </p>
      {(loadErr.geo || loadErr.bonus) && (
        <div className="mb-6 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 flex flex-wrap items-center justify-between gap-3" data-testid="admin-geo-load-error">
          <span>Couldn't load the current {loadErr.geo && loadErr.bonus ? "location settings" : loadErr.geo ? "discovery radius" : "location bonus"}. Saving is turned off until it loads, so nothing is overwritten by mistake.</span>
          <button onClick={loadAll} className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold" data-testid="admin-geo-retry">Try again</button>
        </div>
      )}

      <div className="pf-card">
        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">
          Boost radius (km)
        </label>
        <input
          type="number"
          min="1"
          max="500"
          step="1"
          value={radiusKm}
          onChange={(e) => setRadiusKm(e.target.value)}
          className="w-full max-w-xs px-3 py-2 rounded-lg border border-slate-200 font-mono"
          data-testid="admin-geo-radius-input"
        />

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setEnabled((v) => !v)}
            className="inline-flex items-center gap-2"
            data-testid="admin-geo-toggle"
          >
            {enabled ? (
              <ToggleRight className="w-6 h-6 text-emerald-500" />
            ) : (
              <ToggleLeft className="w-6 h-6 text-slate-400" />
            )}
            <span className="text-sm font-medium text-slate-700">
              {enabled ? "Canada-only geofence enforced" : "Geofence disabled (travellers allowed)"}
            </span>
          </button>
        </div>

        <button
          onClick={save}
          disabled={saving || loadErr.geo || !config}
          data-testid="admin-geo-save"
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving…" : "Save"}
        </button>

        {config?.updated_at && (
          <p className="mt-4 text-xs text-slate-400">
            Last updated {new Date(config.updated_at).toLocaleString()}
            {config.updated_by ? ` · by ${config.updated_by.slice(0, 8)}…` : ""}
          </p>
        )}
      </div>

      <div className="pf-card mt-6" data-testid="admin-location-bonus">
        <div className="flex items-center gap-2 mb-1">
          <MapPin className="w-5 h-5 text-emerald-500" />
          <h2 className="font-display text-lg tracking-tight text-slate-900">Location bonus (RN-I.2)</h2>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          One-time platform-funded cashback bonus awarded on a shopper's
          first verified purchase of a geo-boosted offer. Fires only when
          this is <span className="font-semibold">on</span> AND the shopper
          claimed the offer while within the boost radius.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Bonus amount</label>
            <input
              type="number"
              min="0"
              max="100"
              step="0.01"
              value={bonusForm.amount}
              onChange={(e) => setBonusForm({ ...bonusForm, amount: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 font-mono"
              data-testid="bonus-amount-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Currency</label>
            <input
              type="text"
              maxLength={3}
              value={bonusForm.currency}
              onChange={(e) => setBonusForm({ ...bonusForm, currency: e.target.value.toUpperCase() })}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 font-mono"
              data-testid="bonus-currency-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Min purchase</label>
            <input
              type="number"
              min="0"
              max="1000"
              step="0.01"
              value={bonusForm.min_purchase}
              onChange={(e) => setBonusForm({ ...bonusForm, min_purchase: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 font-mono"
              data-testid="bonus-min-purchase-input"
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1">Shopper-facing message</label>
            <input
              type="text"
              maxLength={140}
              value={bonusForm.message}
              onChange={(e) => setBonusForm({ ...bonusForm, message: e.target.value })}
              className="w-full px-3 py-2 rounded-lg border border-slate-200"
              placeholder="First nearby-offer bonus"
              data-testid="bonus-message-input"
            />
          </div>
        </div>

        <div className="mt-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => setBonusForm({ ...bonusForm, enabled: !bonusForm.enabled })}
            className="inline-flex items-center gap-2"
            data-testid="bonus-toggle"
          >
            {bonusForm.enabled ? (
              <ToggleRight className="w-6 h-6 text-emerald-500" />
            ) : (
              <ToggleLeft className="w-6 h-6 text-slate-400" />
            )}
            <span className="text-sm font-medium text-slate-700">
              {bonusForm.enabled ? "Bonus is ON" : "Bonus is OFF (kill-switch)"}
            </span>
          </button>
        </div>

        <button
          onClick={async () => {
            const amt = Number(bonusForm.amount);
            const mp = Number(bonusForm.min_purchase);
            if (!Number.isFinite(amt) || amt < 0 || amt > 100) {
              toast.error("Amount must be between 0 and 100");
              return;
            }
            if (!Number.isFinite(mp) || mp < 0 || mp > 1000) {
              toast.error("Min purchase must be between 0 and 1000");
              return;
            }
            setBonusSaving(true);
            try {
              const r = await api.patch("/admin/settings/location-bonus", {
                enabled: bonusForm.enabled,
                amount: amt,
                currency: bonusForm.currency || "CAD",
                min_purchase: mp,
                message: bonusForm.message || undefined,
              });
              setBonus(r.data);
              toast.success("Bonus settings saved");
            } catch {
              toast.error("Save failed");
            } finally {
              setBonusSaving(false);
            }
          }}
          disabled={bonusSaving || loadErr.bonus || !bonus}
          data-testid="bonus-save"
          className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {bonusSaving ? "Saving…" : "Save bonus"}
        </button>

        {bonus?.updated_at && (
          <p className="mt-4 text-xs text-slate-400">
            Last updated {new Date(bonus.updated_at).toLocaleString()}
          </p>
        )}
      </div>
    </div>
  );
}
